/**
 * Shared pieces for the legal-sources citation generators.
 *
 * Legal citation has conventions the general helpers in `citation-parts.ts` do not
 * cover: Bluebook ordinals ("102d Cong."), digit grouping in five-digit numbers
 * ("Exec. Order No. 13,676"), abbreviated months ("Sept. 23, 2014"), Roman-numeral
 * articles, and MLA's shortened page ranges. They live here so the eight generators
 * write them the same way.
 *
 * The vocabulary of a citation format — "Cong.", "Fed. Reg.", "testimony of", month
 * names — is format data, like the English month names in `citation-parts.ts`, and
 * stays in code. Everything the reader sees around the citation (labels, notes) comes
 * from the widget's strings.
 */

import type { CitationParts, PartialDate } from "@/lib/tool-kit/citation-parts";

/** Field values as the builder stores them. */
export type LegalValues = Readonly<Record<string, string | undefined>>;

/** A field's trimmed value, or "". */
export function get(values: LegalValues, id: string): string {
  return (values[id] ?? "").trim();
}

/** An upright (non-italic) fragment of a citation. */
export function plain(text: string): CitationParts[number] {
  return { text };
}

/** An italic fragment of a citation. */
export function italic(text: string): CitationParts[number] {
  return { text, italic: true };
}

const RE_DIGITS = /^\d+$/;
const RE_GROUP_THOUSANDS = /\B(?=(\d{3})+(?!\d))/g;

/**
 * Bluebook rule 6.2(a): commas separate groups of three digits in numbers of five or
 * more digits — "Exec. Order No. 13,676", "55 Fed. Reg. 52,782" — but not in four-digit
 * numbers ("9981", "7619"). Anything that is not a plain number is returned as typed.
 */
export function groupDigits(value: string): string {
  const trimmed = value.trim();
  if (!RE_DIGITS.test(trimmed) || trimmed.length < 5) {
    return trimmed;
  }
  return trimmed.replace(RE_GROUP_THOUSANDS, ",");
}

/** A whole number typed into a field, or null. */
export function wholeNumber(value: string): number | null {
  const trimmed = value.trim();
  if (!RE_DIGITS.test(trimmed)) {
    return null;
  }
  const number = Number(trimmed);
  return number > 0 ? number : null;
}

/**
 * Ordinals inside a legal citation (Bluebook rule 6.2(b)(ii)): "2d" and "3d", not
 * "2nd" and "3rd" — "102d Cong.", "113th Cong.", "111th Cong.".
 */
export function legalOrdinal(n: number): string {
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) {
    return `${n}th`;
  }
  const last = n % 10;
  if (last === 1) {
    return `${n}st`;
  }
  if (last === 2 || last === 3) {
    return `${n}d`;
  }
  return `${n}th`;
}

/** Ordinals in prose and in MLA entries: "2nd session", "113th Congress". */
export function englishOrdinal(n: number): string {
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) {
    return `${n}th`;
  }
  const suffixes = ["th", "st", "nd", "rd"];
  const last = n % 10;
  return `${n}${last <= 3 ? suffixes[last] : "th"}`;
}

const ROMAN_VALUES: readonly [number, string][] = [
  [1000, "M"],
  [900, "CM"],
  [500, "D"],
  [400, "CD"],
  [100, "C"],
  [90, "XC"],
  [50, "L"],
  [40, "XL"],
  [10, "X"],
  [9, "IX"],
  [5, "V"],
  [4, "IV"],
  [1, "I"],
];

export function toRoman(value: number): string {
  let rest = value;
  let out = "";
  for (const [amount, symbol] of ROMAN_VALUES) {
    while (rest >= amount) {
      out += symbol;
      rest -= amount;
    }
  }
  return out;
}

const RE_ROMAN = /^[ivxlcdm]+$/i;

/** "14" or "xiv" → "XIV". Anything else (e.g. "1-A") is returned as typed. */
export function romanNumeral(value: string): string {
  const trimmed = value.trim();
  const number = wholeNumber(trimmed);
  if (number !== null && number < 4000) {
    return toRoman(number);
  }
  if (RE_ROMAN.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  return trimmed;
}

const FULL_MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** Bluebook table T12: Jan., Feb., Mar., Apr., May, June, July, Aug., Sept., Oct., Nov., Dec. */
const BLUEBOOK_MONTHS = [
  "Jan.",
  "Feb.",
  "Mar.",
  "Apr.",
  "May",
  "June",
  "July",
  "Aug.",
  "Sept.",
  "Oct.",
  "Nov.",
  "Dec.",
];

function monthDayYear(date: PartialDate, months: readonly string[]): string {
  if (!date.month) {
    return String(date.year);
  }
  const month = months[date.month - 1];
  return date.day
    ? `${month} ${date.day}, ${date.year}`
    : `${month} ${date.year}`;
}

/** "September 23, 2014" — APA and Chicago. Empty when there is no date. */
export function fullDate(date: PartialDate | null): string {
  return date ? monthDayYear(date, FULL_MONTHS) : "";
}

/** "Sept. 23, 2014" — Bluebook. Empty when there is no date. */
export function bluebookDate(date: PartialDate | null): string {
  return date ? monthDayYear(date, BLUEBOOK_MONTHS) : "";
}

/** "27 April 2016" — OSCOLA and Cite Them Right. Empty when there is no date. */
export function dayMonthYear(date: PartialDate | null): string {
  if (!date) {
    return "";
  }
  if (!date.month) {
    return String(date.year);
  }
  const month = FULL_MONTHS[date.month - 1];
  return date.day
    ? `${date.day} ${month} ${date.year}`
    : `${month} ${date.year}`;
}

/**
 * MLA 9 page ranges: the second number in full through 99; above that, only as many
 * final digits as differ, and never fewer than two — "6405–10", "38516–612",
 * "1608–774", "96–101".
 */
export function mlaPageRange(first: string, last: string): string {
  const from = first.trim();
  const to = last.trim();
  if (from === "") {
    return "";
  }
  if (to === "" || to === from) {
    return from;
  }
  const bothNumbers = RE_DIGITS.test(from) && RE_DIGITS.test(to);
  if (
    !bothNumbers ||
    Number(from) < 100 ||
    Number(to) < Number(from) ||
    to.length !== from.length
  ) {
    return `${from}–${to}`;
  }
  let shared = 0;
  while (shared < from.length - 2 && from[shared] === to[shared]) {
    shared += 1;
  }
  return `${from}–${to.slice(shared)}`;
}

const RE_ENDS_SENTENCE = /[.?!]["”’)]*$/;

/** The punctuation that closes an element: "." unless the text already ends a sentence. */
export function closer(text: string): string {
  return RE_ENDS_SENTENCE.test(text.trim()) ? "" : ".";
}

const RE_PROTOCOL = /^https?:\/\//i;

/** MLA 9 drops "http://" and "https://" from URLs. */
export function mlaUrl(url: string): string {
  return url.trim().replace(RE_PROTOCOL, "");
}

/*
 * Title case for MLA and Chicago, which capitalize the principal words of a title.
 *
 * It only ever capitalizes. A word the user typed with a capital (a proper noun, an
 * acronym, "eCFR") is left alone, so the safe way to type a title is sentence case —
 * which is also exactly what APA wants. Articles, the coordinating conjunctions both
 * styles lowercase, "to", "as" and common prepositions stay lowercase unless they open
 * the title, open a subtitle or end it.
 */
const MINOR_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "but",
  "or",
  "nor",
  "for",
  "as",
  "to",
  "at",
  "by",
  "from",
  "in",
  "into",
  "of",
  "on",
  "onto",
  "per",
  "via",
  "with",
  "within",
  "without",
  "upon",
  "about",
  "across",
  "against",
  "among",
  "between",
  "through",
  "toward",
  "towards",
  "under",
]);

const RE_LOWER_WORD = /^[^\p{L}]*\p{Ll}[\p{Ll}'’-]*[^\p{L}]*$/u;
const RE_FIRST_LETTER = /\p{L}/u;
const RE_SUBTITLE_BREAK = /[:—?!]$/;
const RE_NON_LETTERS = /[^\p{L}]/gu;

function capitalizeFirstLetter(word: string): string {
  const index = word.search(RE_FIRST_LETTER);
  if (index < 0) {
    return word;
  }
  return (
    word.slice(0, index) +
    word.charAt(index).toUpperCase() +
    word.slice(index + 1)
  );
}

function capitalizeSegments(word: string, forced: boolean): string {
  const segments = word.split("-");
  return segments
    .map((segment, index) => {
      const bare = segment.replace(RE_NON_LETTERS, "").toLowerCase();
      if (index > 0 && MINOR_WORDS.has(bare)) {
        return segment;
      }
      if (index === 0 && !forced && MINOR_WORDS.has(bare)) {
        return segment;
      }
      return capitalizeFirstLetter(segment);
    })
    .join("-");
}

/** Prefixes after which a hyphenated word stays lowercase: "Anti-inflammatory". */
const LOWER_PREFIXES = new Set([
  "anti",
  "co",
  "multi",
  "non",
  "post",
  "pre",
  "re",
  "semi",
  "sub",
]);

const SMALL_AFTER_HYPHEN = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "by",
  "for",
  "in",
  "of",
  "on",
  "or",
  "the",
  "to",
]);

const RE_HYPHENATED = /^([\p{L}]+)-(\p{Ll}[\p{L}]*)$/u;

/** "Full-day" → "Full-Day", leaving prefixes ("Co-author") and small words alone. */
function capitalizeAfterHyphen(word: string): string {
  const match = RE_HYPHENATED.exec(word);
  if (!match) {
    return word;
  }
  const [, head, tail] = match;
  if (LOWER_PREFIXES.has(head.toLowerCase()) || SMALL_AFTER_HYPHEN.has(tail)) {
    return word;
  }
  return `${head}-${tail.charAt(0).toUpperCase()}${tail.slice(1)}`;
}

/**
 * Title case for citation titles: minor words stay lower case except first, last and
 * after a subtitle break, and the second part of a hyphenated compound is capitalized —
 * "Full-Day Kindergarten", "Cross-Sectional" — except after a prefix such as anti-, co-
 * or non-. A word that already starts with a capital is otherwise left alone, so the
 * compound's second part needs its own pass.
 */
export function titleCase(text: string): string {
  const words = text.trim().split(" ");
  let forceNext = true;
  return words
    .map((word, index) => {
      const forced = forceNext || index === words.length - 1;
      forceNext = RE_SUBTITLE_BREAK.test(word);
      if (word === "" || !RE_LOWER_WORD.test(word)) {
        return capitalizeAfterHyphen(word);
      }
      return capitalizeAfterHyphen(capitalizeSegments(word, forced));
    })
    .join(" ");
}

const RE_AFTER_COLON = /: (\p{Ll})/gu;

/** Every style capitalizes the first word of a subtitle: "world: The 2030 Agenda". */
export function capitalizeSubtitle(text: string): string {
  return text.replace(RE_AFTER_COLON, (_match, letter: string) => {
    return `: ${letter.toUpperCase()}`;
  });
}

/** The title before any subtitle — the shortened form MLA and APA use in text. */
export function mainTitle(text: string): string {
  const colon = text.indexOf(":");
  return (colon > 0 ? text.slice(0, colon) : text).trim();
}
